import { runInAction, set } from 'mobx';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { IChannelBudgets, IRecommendedSplit, OmniCampaign } from '@feathr/blackbox';
import { EIntegrationTypes } from '@feathr/blackbox';

import {
  calculateChannelBudgets,
  calculateRecommendedSplit,
  getAllocationPercentage,
  getMetaAllocationDollars,
} from './ChannelBudgetSplit.utils';

/** Params for `useChannelBudgetSplit`. */
interface IUseChannelBudgetSplitParams {
  /** The campaign whose exposure_settings hold the persisted channel split. */
  campaign: OmniCampaign;
  /** Backend per-channel minimum budgets used to keep the split above the floor. */
  channelMinimums?: IChannelBudgets | null;
  /** Backend-recommended split; falls back to 60/40 when null. */
  recommendedSplit?: IRecommendedSplit | null;
  /** Backend-recommended per-channel dollar amounts, shown as-is in recommended mode. */
  recommendedAmounts?: { display: number; meta: number } | null;
}

/** Which control drives the split: the recommendation or a manual value. */
export type TChannelSplitMode = 'recommended' | 'manual';

/** Even split used as the neutral starting point when switching to manual. */
export const MANUAL_SPLIT_DEFAULT = 50;

/** Return value of `useChannelBudgetSplit`. */
interface IUseChannelBudgetSplitReturn {
  /** Current slider value (display channel percentage). */
  displayPercent: number;
  /** Raw slider value setter (does not change mode; used internally/in tests). */
  setDisplayPercent: (value: number) => void;
  /** Live slider drag callback: adopts the value and switches to manual on first move. */
  handleSliderChange: (value: number) => void;
  /** Dollar amount allocated to the display channel. */
  displayBudget: number;
  /** Dollar amount allocated to the meta channel. */
  metaBudget: number;
  /** Meta's dollars from the split percentage, which is what gets saved; `metaBudget` may differ. */
  percentMetaBudget: number;
  /** Display's dollars from the split percentage, which pair with `percentMetaBudget`. */
  percentDisplayBudget: number;
  /** Whether to render the audience-drift warning Alert. */
  showAudienceDriftWarning: boolean;
  /** Persist the slider value on release. */
  handleSliderChangeEnd: (value: number) => void;
  /** Reset the slider to the recommended split and persist. */
  handleUseRecommended: () => void;
  /** Switch to manual control, snapping to an even split to drag from. */
  handleSetManual: () => void;
  /**
   * Which control is active. Tracked explicitly rather than inferred from the
   * slider value, since the recommended split can itself land on the manual
   * default (e.g. a tight budget clamps the recommendation to 50/50).
   */
  mode: TChannelSplitMode;
  /** Audience-driven recommended display percentage. */
  recommendedDisplayPercent: number;
  /**
   * Whether the split controls should be disabled. At (or below) the minimum
   * budget the per-channel minimums consume the whole budget, so the only valid
   * split is the recommended one — there's nothing to adjust.
   */
  isSplitLocked: boolean;
}

/**
 * Encapsulates all state management, effects, and MobX persistence logic
 * for the ChannelBudgetSplit component.
 *
 * @param params - Campaign and audience data.
 * @returns Values and handlers consumed by the presentation component.
 */
export function useChannelBudgetSplit({
  campaign,
  channelMinimums,
  recommendedSplit,
  recommendedAmounts,
}: IUseChannelBudgetSplitParams): IUseChannelBudgetSplitReturn {
  const totalBudget = campaign.get('exposure_settings')?.target_value ?? 0;
  const recommendedTradedesk = recommendedSplit?.display;

  const recommended = useMemo(
    () => calculateRecommendedSplit(recommendedTradedesk),
    [recommendedTradedesk],
  );

  const persistedSplit = getAllocationPercentage(
    campaign.get('exposure_settings')?.channel_split?.allocations,
    EIntegrationTypes.Tradedesk,
  );

  // Show the persisted split as-is; the validator and inline alert flag
  // invalid allocations so the UI doesn't silently rewrite saved data.
  const initialDisplay = persistedSplit ?? recommended.tradedesk;
  const [displayPercent, setDisplayPercent] = useState<number>(initialDisplay);

  /*
   * At (or below) the minimum budget the per-channel minimums consume the whole
   * budget, so only the recommended split is valid — lock the controls.
   */
  const isSplitLocked = !!channelMinimums && totalBudget <= channelMinimums.total;

  /*
   * An explicit click overrides the derived mode. This is what keeps the
   * toggle switchable when the recommended split coincides with the manual
   * default (e.g. a tight budget clamps the recommendation to 50/50) — without
   * it, the mode would snap back to whatever the value happens to match.
   */
  const [userMode, setUserMode] = useState<TChannelSplitMode | null>(null);

  /*
   * Absent an explicit choice, reflect reality: a campaign whose split matches
   * the recommendation (or has no split yet) shows "recommended"; a custom
   * split shows "manual".
   */
  const derivedMode: TChannelSplitMode =
    persistedSplit === undefined || persistedSplit === recommended.tradedesk
      ? 'recommended'
      : 'manual';
  const mode = userMode ?? derivedMode;

  /*
   * The recommendation from the previous render, so we can tell whether the
   * slider is currently tracking the recommendation (follow a new one) versus
   * sitting on a value the user set manually (leave it alone).
   */
  const prevRecommendedRef = useRef(recommended.tradedesk);

  /*
   * Keep the split on the recommendation while it's tracking it — either the
   * user is explicitly in recommended mode, or they haven't gone manual and the
   * slider still sits on the previous recommendation. This makes recommended
   * mode "sticky": when the budget re-fetches a new split the slider follows it
   * instead of flipping to manual because the old value no longer matches. A
   * manual value (or a saved split that never matched) is left alone.
   */
  useEffect(() => {
    const followingRecommendation =
      userMode === 'recommended' ||
      (userMode !== 'manual' && displayPercent === prevRecommendedRef.current);
    if (followingRecommendation && displayPercent !== recommended.tradedesk) {
      setDisplayPercent(recommended.tradedesk);
      // Persist so a saved split stays in sync; skip for a not-yet-persisted
      // fresh campaign so we don't dirty it before any user action.
      if (persistedSplit !== undefined || userMode === 'recommended') {
        persistSplit(recommended.tradedesk);
      }
    }
    prevRecommendedRef.current = recommended.tradedesk;
    // persistSplit is stable enough for this guarded, self-terminating effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userMode, recommended.tradedesk, displayPercent, persistedSplit]);

  /*
   * On a fresh dual-channel campaign the recommended split is shown as the
   * active selection, but nothing is written to the model yet — so the step
   * validator reports "configure a budget split" even though the user sees a
   * valid split. Once the backend recommendation has loaded, persist it so the
   * shown selection is real and the user can advance without an explicit click.
   */
  useEffect(() => {
    if (persistedSplit === undefined && userMode !== 'manual' && recommendedSplit) {
      persistSplit(recommended.tradedesk);
    }
    // persistSplit is stable enough for this guarded, self-terminating effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistedSplit, userMode, recommendedSplit, recommended.tradedesk]);

  /*
   * When locked (budget at the minimum), force the recommended split so the
   * locked-in value is always the valid per-channel-minimum allocation, even if
   * a stale manual split was persisted at a higher budget.
   */
  useEffect(() => {
    if (isSplitLocked && displayPercent !== recommended.tradedesk) {
      setDisplayPercent(recommended.tradedesk);
      setUserMode('recommended');
      persistSplit(recommended.tradedesk);
    }
    // persistSplit is stable enough for this guarded, self-terminating effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSplitLocked, recommended.tradedesk, displayPercent]);

  const { displayBudget: derivedDisplayBudget, metaBudget: derivedMetaBudget } =
    calculateChannelBudgets(totalBudget, displayPercent);

  /*
   * In recommended mode, show the backend's exact per-channel amounts (which sum
   * to the budget) rather than the whole-percent-derived dollars — the percent
   * is rounded and can dip a channel below its floor (e.g. 39% of $38 = $14.82,
   * under the $15 Display minimum). Manual mode is the only place the
   * fractional, percent-derived dollars are shown. When locked without loaded
   * amounts, fall back to the per-channel minimums (equal to the amounts there).
   */
  let displayBudget = derivedDisplayBudget;
  let metaBudget = derivedMetaBudget;
  if (mode === 'recommended' && recommendedAmounts) {
    displayBudget = recommendedAmounts.display;
    metaBudget = recommendedAmounts.meta;
  } else if (isSplitLocked && channelMinimums) {
    displayBudget = channelMinimums.display;
    metaBudget = channelMinimums.meta;
  }

  /*
   * TODO: Revisit audience drift warning with a better signal — detect
   * audience *selection* changes rather than reach-number drift, which
   * produces false positives on page load for returning users.
   */
  const showAudienceDriftWarning = false;

  /** Write the given display percentage to the campaign's exposure_settings via MobX. */
  function persistSplit(tradedesk: number): void {
    const exposureSettings = campaign.get('exposure_settings');
    if (!exposureSettings) {
      return;
    }
    runInAction(() => {
      set(exposureSettings, {
        channel_split: {
          allocations: [
            { channel: EIntegrationTypes.Tradedesk, percentage: tradedesk },
            { channel: EIntegrationTypes.Meta, percentage: 100 - tradedesk },
          ],
        },
      });
      campaign.setAttributeDirty('exposure_settings');
    });
  }

  /*
   * Live slider drag. The first movement switches to manual so the drag isn't
   * fought by the follow-recommendation effect (which otherwise snaps the thumb
   * back to the recommendation) — the value and dollars then track the thumb
   * live. Persistence happens on release via handleSliderChangeEnd.
   */
  function handleSliderChange(value: number): void {
    if (value !== displayPercent && mode !== 'manual') {
      setUserMode('manual');
    }
    setDisplayPercent(value);
  }

  function handleSliderChangeEnd(value: number): void {
    setUserMode('manual');
    persistSplit(value);
  }

  function handleUseRecommended(): void {
    setUserMode('recommended');
    setDisplayPercent(recommended.tradedesk);
    persistSplit(recommended.tradedesk);
  }

  function handleSetManual(): void {
    setUserMode('manual');
    setDisplayPercent(MANUAL_SPLIT_DEFAULT);
    persistSplit(MANUAL_SPLIT_DEFAULT);
  }

  return {
    displayPercent,
    setDisplayPercent,
    handleSliderChange,
    displayBudget,
    metaBudget,
    percentMetaBudget: getMetaAllocationDollars(totalBudget, 100 - displayPercent),
    percentDisplayBudget: derivedDisplayBudget,
    showAudienceDriftWarning,
    handleSliderChangeEnd,
    handleUseRecommended,
    handleSetManual,
    mode,
    recommendedDisplayPercent: recommended.tradedesk,
    isSplitLocked,
  };
}
